import { ChevronDown, ChevronUp } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SideBySideDiff } from './TextDiff'
import {
  formatValueForDisplay,
  isComplexValue,
  type FieldDiffResult,
} from './diffUtils'
import { getTranslatedFieldLabel } from './diffConfig'

interface FieldDiffProps {
  /** The diff result for this field */
  diff: FieldDiffResult
  /** Whether to show expanded by default */
  defaultExpanded?: boolean
}

/**
 * Renders a single field diff with appropriate display based on config
 *
 * For fields with showFullDiff: true, displays side-by-side comparison
 * For fields with showFullDiff: false, displays a simple "changed" indicator with values
 */
export function FieldDiff({ diff, defaultExpanded = true }: FieldDiffProps) {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)

  const { config, previousValue, currentValue } = diff

  // Format values for display
  const prevDisplay = formatValueForDisplay(previousValue)
  const currDisplay = formatValueForDisplay(currentValue)

  // Get translated field label
  const fieldLabel = getTranslatedFieldLabel(diff.fieldPath, t)

  // Object/array values are unreadable as a truncated single line, so show them
  // as a side-by-side JSON diff (with highlighting) too.
  const isJsonValue =
    isComplexValue(previousValue) || isComplexValue(currentValue)
  const useSideBySide = config.showFullDiff || isJsonValue
  const monospace =
    isJsonValue ||
    diff.fieldPath === 'config' ||
    diff.fieldPath.startsWith('config.')

  // For fields that show full diff, use side-by-side view
  if (useSideBySide) {
    return (
      <div className="border-b border-gray-200 py-4 last:border-b-0 dark:border-gray-700">
        {/* Header with expand toggle */}
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex w-full items-center justify-between text-left"
        >
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900 dark:text-gray-100">
              {fieldLabel}
            </span>
            <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
              {t('resources.assistants.revisions.field_changed')}
            </span>
          </div>
          {isExpanded ? (
            <ChevronUp className="h-4 w-4 text-gray-500" />
          ) : (
            <ChevronDown className="h-4 w-4 text-gray-500" />
          )}
        </button>

        {/* Expanded content */}
        {isExpanded && (
          <div className="mt-4">
            <SideBySideDiff
              previousValue={prevDisplay}
              currentValue={currDisplay}
              segments={diff.diffSegments}
              monospace={monospace}
            />
          </div>
        )}
      </div>
    )
  }

  // For fields that only show change indicator
  return (
    <div className="border-b border-gray-200 py-4 last:border-b-0 dark:border-gray-700">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-900 dark:text-gray-100">
            {fieldLabel}
          </span>
          <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
            {t('resources.assistants.revisions.field_changed')}
          </span>
        </div>
      </div>

      {/* Simple value comparison for non-full-diff fields */}
      <div className="mt-2 grid grid-cols-2 gap-4 text-sm">
        <div className="text-gray-600 dark:text-gray-400">
          <span className="text-xs font-medium uppercase text-red-600 dark:text-red-400">
            {t('resources.assistants.revisions.comparison.before')}:{' '}
          </span>
          <span className="text-gray-800 dark:text-gray-200">
            {truncateValue(prevDisplay, 100)}
          </span>
        </div>
        <div className="text-gray-600 dark:text-gray-400">
          <span className="text-xs font-medium uppercase text-green-600 dark:text-green-400">
            {t('resources.assistants.revisions.comparison.after')}:{' '}
          </span>
          <span className="text-gray-800 dark:text-gray-200">
            {truncateValue(currDisplay, 100)}
          </span>
        </div>
      </div>
    </div>
  )
}

/**
 * Truncate a value to a maximum length with ellipsis
 */
function truncateValue(value: string, maxLength: number): string {
  if (value.length <= maxLength) return value
  return value.substring(0, maxLength) + '...'
}

interface FieldDiffGroupProps {
  /** Category label */
  label: string
  /** Diffs in this category */
  diffs: FieldDiffResult[]
  /** Whether to show expanded by default */
  defaultExpanded?: boolean
}

/**
 * Renders a group of field diffs with a category header
 */
export function FieldDiffGroup({
  label,
  diffs,
  defaultExpanded = true,
}: FieldDiffGroupProps) {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)

  if (diffs.length === 0) {
    return null
  }

  const changeText = diffs.length === 1
    ? t('resources.assistants.revisions.comparison.change', { count: diffs.length })
    : t('resources.assistants.revisions.comparison.change_plural', { count: diffs.length })

  return (
    <div className="mb-6">
      {/* Category header */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="mb-3 flex w-full items-center justify-between rounded-lg bg-gray-100 px-4 py-2 text-left dark:bg-gray-800"
      >
        <div className="flex items-center gap-2">
          <span className="font-semibold text-gray-900 dark:text-gray-100">
            {label}
          </span>
          <span className="inline-flex items-center rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-300">
            {changeText}
          </span>
        </div>
        {isExpanded ? (
          <ChevronUp className="h-4 w-4 text-gray-500" />
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-500" />
        )}
      </button>

      {/* Diffs */}
      {isExpanded && (
        <div className="rounded-lg border border-gray-200 bg-white px-4 dark:border-gray-700 dark:bg-gray-900">
          {diffs.map((diff) => (
            <FieldDiff key={diff.fieldPath} diff={diff} defaultExpanded={true} />
          ))}
        </div>
      )}
    </div>
  )
}

export default FieldDiff
